import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  LayoutGrid,
  LayoutDashboard,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  GripVertical,
  Loader2,
  ChevronRight,
  ChevronDown,
  StickyNote,
  Wand2,
  Sparkles,
} from 'lucide-react';
import { PageHeader } from './ui/page-header';
import { EmptyState } from './ui/empty-state';
import { api, AiLimitError, type BoardColumn, type BoardCard } from '../lib/api';
import { STORY_STRUCTURES, type StoryStructure } from '../lib/story-structures';
import { toast } from '../lib/toast';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
  ResponsiveModalFooter,
} from './ui/responsive-modal';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { useAppStore } from '../stores/app-store';
import { BookCover } from './book-cover';
import { useIsMobile } from '@/hooks/use-mobile';

// ============================================
// Card color palette
// ============================================

const CARD_COLORS = [
  { value: null, label: 'Sem cor', bg: 'bg-card', border: 'border-border' },
  { value: 'amber', label: 'Âmbar', bg: 'bg-amber-500/15', border: 'border-amber-500/40' },
  { value: 'blue', label: 'Azul', bg: 'bg-blue-500/15', border: 'border-blue-500/40' },
  { value: 'green', label: 'Verde', bg: 'bg-green-500/15', border: 'border-green-500/40' },
  { value: 'red', label: 'Vermelho', bg: 'bg-red-500/15', border: 'border-red-500/40' },
  { value: 'purple', label: 'Roxo', bg: 'bg-purple-500/15', border: 'border-purple-500/40' },
  { value: 'pink', label: 'Rosa', bg: 'bg-pink-500/15', border: 'border-pink-500/40' },
  { value: 'cyan', label: 'Ciano', bg: 'bg-cyan-500/15', border: 'border-cyan-500/40' },
] as const;

type CardColorValue = (typeof CARD_COLORS)[number]['value'];

function getCardColorClasses(color: string | null) {
  const found = CARD_COLORS.find((c) => c.value === color);
  return found ?? CARD_COLORS[0];
}

// i18n keys for each card color label (CARD_COLORS.label holds the pt-BR fallback)
const CARD_COLOR_I18N_KEYS: Record<string, string> = {
  null: 'outlineBoard.colorNone',
  amber: 'outlineBoard.colorAmber',
  blue: 'outlineBoard.colorBlue',
  green: 'outlineBoard.colorGreen',
  red: 'outlineBoard.colorRed',
  purple: 'outlineBoard.colorPurple',
  pink: 'outlineBoard.colorPink',
  cyan: 'outlineBoard.colorCyan',
};

function getCardColorLabel(t: (key: string, def: string) => string, value: CardColorValue): string {
  const entry = CARD_COLORS.find((c) => c.value === value) ?? CARD_COLORS[0];
  const key = CARD_COLOR_I18N_KEYS[String(value)];
  return t(key, entry.label);
}

// ============================================
// Timeline View (read-only horizontal beat strip)
// ============================================

// Subtle accent colours cycling per column group
// trio do mock: azul (setup) · roxo (confronto) · âmbar (resolução), ciclando
const COLUMN_ACCENTS = [
  'border-[rgba(91,141,239,0.35)] bg-[rgba(91,141,239,0.07)]',
  'border-[rgba(167,139,250,0.35)] bg-[rgba(167,139,250,0.07)]',
  'border-[rgba(223,201,147,0.35)] bg-[rgba(223,201,147,0.06)]',
] as const;


// ============================================
// Template Picker Modal
// ============================================

interface TemplatePickerModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (template: StoryStructure, mode: 'replace' | 'append') => Promise<void>;
  hasExistingContent: boolean;
  applying: boolean;
}

function TemplatePickerModal({
  open,
  onClose,
  onApply,
  hasExistingContent,
  applying,
}: TemplatePickerModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'pick' | 'confirm'>('pick');
  const [selected, setSelected] = useState<StoryStructure | null>(null);

  // Reset state every time the modal opens
  useEffect(() => {
    if (open) {
      setStep('pick');
      setSelected(null);
    }
  }, [open]);

  const handlePickTemplate = async (template: StoryStructure) => {
    if (applying) return;
    if (hasExistingContent) {
      setSelected(template);
      setStep('confirm');
    } else {
      // Board is empty — apply directly without confirmation
      await onApply(template, 'append');
      onClose();
    }
  };

  const handleConfirm = async (mode: 'replace' | 'append') => {
    if (!selected || applying) return;
    await onApply(selected, mode);
    onClose();
  };

  return (
    <ResponsiveModal open={open} onOpenChange={(o) => !o && onClose()}>
      <ResponsiveModalContent className="sm:max-w-lg">
        {step === 'pick' ? (
          <>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>{t('outlineBoard.pickStructureTitle', 'Escolher estrutura narrativa')}</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                {t('outlineBoard.pickStructureDescription', 'Selecione um modelo para preencher o quadro com beats prontos.')}
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>

            <div className="space-y-3">
              {STORY_STRUCTURES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => void handlePickTemplate(s)}
                  disabled={applying}
                  className="w-full text-left rounded-xl border border-border p-4 hover:border-primary/60 hover:bg-primary/5 transition-all disabled:opacity-50"
                >
                  <p className="font-semibold text-foreground text-sm">
                    {t(`storyStructures.${s.id}.name`, s.name)}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t(`storyStructures.${s.id}.description`, s.description)}
                  </p>
                  <div className="flex flex-wrap gap-1 mt-2.5">
                    {s.columns.map((col, colIndex) => (
                      <span
                        key={col.title}
                        className="text-[10px] bg-muted rounded px-1.5 py-0.5 text-muted-foreground"
                      >
                        {t(`storyStructures.${s.id}.columns.${colIndex}.title`, col.title)} ·{' '}
                        {t('outlineBoard.beatsCount', '{{n}} beats', { n: col.beats.length })}
                      </span>
                    ))}
                  </div>
                </button>
              ))}
            </div>

            <ResponsiveModalFooter>
              <Button variant="outline" onClick={onClose} disabled={applying}>
                {t('outlineBoard.cancel', 'Cancelar')}
              </Button>
            </ResponsiveModalFooter>
          </>
        ) : (
          <>
            <ResponsiveModalHeader>
              <ResponsiveModalTitle>{t('outlineBoard.boardHasContentTitle', 'O quadro já tem conteúdo')}</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                {t('outlineBoard.howApplyTemplate', 'Como deseja aplicar “{{name}}”?', {
                  name: selected ? t(`storyStructures.${selected.id}.name`, selected.name) : '',
                })}
              </ResponsiveModalDescription>
            </ResponsiveModalHeader>

            <div className="space-y-3">
              <button
                onClick={() => void handleConfirm('append')}
                disabled={applying}
                className="w-full text-left rounded-xl border border-border p-4 hover:border-primary/60 hover:bg-primary/5 transition-all disabled:opacity-50"
              >
                <p className="font-semibold text-foreground text-sm">{t('outlineBoard.addToEnd', 'Adicionar ao final')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('outlineBoard.addToEndDescription', 'Mantém as colunas existentes e adiciona as novas colunas do template ao final.')}
                </p>
              </button>

              <button
                onClick={() => void handleConfirm('replace')}
                disabled={applying}
                className="w-full text-left rounded-xl border border-destructive/40 p-4 hover:border-destructive/70 hover:bg-destructive/5 transition-all disabled:opacity-50"
              >
                <p className="font-semibold text-destructive text-sm">{t('outlineBoard.replaceAll', 'Substituir tudo')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('outlineBoard.replaceAllDescription', 'Remove todas as colunas e cartões existentes e recria com o template selecionado.')}
                </p>
              </button>
            </div>

            <ResponsiveModalFooter>
              <Button
                variant="outline"
                onClick={() => setStep('pick')}
                disabled={applying}
              >
                {t('outlineBoard.back', 'Voltar')}
              </Button>
              <Button variant="outline" onClick={onClose} disabled={applying}>
                {t('outlineBoard.cancel', 'Cancelar')}
              </Button>
            </ResponsiveModalFooter>
          </>
        )}
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}

// ============================================
// Card Edit Modal
// ============================================

interface CardEditModalProps {
  card: BoardCard | null;
  open: boolean;
  onClose: () => void;
  onSave: (data: { title: string; notes: string | null; color: string | null; chapterId: string | null }) => Promise<void>;
  chapters: Array<{ id: string; title: string }>;
}

function CardEditModal({ card, open, onClose, onSave, chapters }: CardEditModalProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [color, setColor] = useState<CardColorValue>(null);
  const [chapterId, setChapterId] = useState<string | null>('none');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (card) {
      setTitle(card.title);
      setNotes(card.notes ?? '');
      setColor((card.color as CardColorValue) ?? null);
      setChapterId(card.chapterId ?? 'none');
    } else {
      setTitle('');
      setNotes('');
      setColor(null);
      setChapterId('none');
    }
  }, [card]);

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        notes: notes.trim() || null,
        color: color ?? null,
        chapterId: chapterId === 'none' ? null : (chapterId ?? null),
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <ResponsiveModal open={open} onOpenChange={(o) => !o && onClose()}>
      <ResponsiveModalContent className="sm:max-w-md">
        <ResponsiveModalHeader>
          <ResponsiveModalTitle>
            {card ? t('outlineBoard.editScene', 'Editar cena') : t('outlineBoard.newScene', 'Nova cena')}
          </ResponsiveModalTitle>
          <ResponsiveModalDescription>
            {card
              ? t('outlineBoard.editSceneDescription', 'Edite os detalhes desta cena.')
              : t('outlineBoard.newSceneDescription', 'Adicione uma nova cena ao quadro.')}
          </ResponsiveModalDescription>
        </ResponsiveModalHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="card-title">{t('outlineBoard.titleLabel', 'Título')}</Label>
            <Input
              id="card-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('outlineBoard.titlePlaceholder', 'Ex: O herói parte em jornada...')}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="card-notes">{t('outlineBoard.notesLabel', 'Notas')}</Label>
            <Textarea
              id="card-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('outlineBoard.notesPlaceholder', 'Resumo, ideias, lembretes...')}
              rows={3}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t('outlineBoard.cardColorLabel', 'Cor do cartão')}</Label>
            <div className="flex flex-wrap gap-2">
              {CARD_COLORS.map((c) => (
                <button
                  key={String(c.value)}
                  onClick={() => setColor(c.value)}
                  className={cn(
                    'h-7 w-7 rounded-full border-2 transition-all',
                    c.value === null ? 'bg-card border-border' : `${c.bg} ${c.border}`,
                    color === c.value ? 'ring-2 ring-primary ring-offset-2' : 'hover:scale-110'
                  )}
                  title={getCardColorLabel(t, c.value)}
                />
              ))}
            </div>
          </div>

          {chapters.length > 0 && (
            <div className="space-y-1.5">
              <Label htmlFor="card-chapter">{t('outlineBoard.linkToChapterLabel', 'Vincular a capítulo')}</Label>
              <Select value={chapterId ?? 'none'} onValueChange={(v) => setChapterId(v)}>
                <SelectTrigger id="card-chapter">
                  <SelectValue placeholder={t('outlineBoard.noChapter', 'Nenhum capítulo')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('outlineBoard.noChapter', 'Nenhum capítulo')}</SelectItem>
                  {chapters.map((ch) => (
                    <SelectItem key={ch.id} value={ch.id}>
                      {ch.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <ResponsiveModalFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('outlineBoard.cancel', 'Cancelar')}
          </Button>
          <Button onClick={handleSave} disabled={saving || !title.trim()}>
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
            {t('outlineBoard.save', 'Salvar')}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}

// ============================================
// Board Card (sortable)
// ============================================

interface BoardCardItemProps {
  card: BoardCard;
  columnId: string;
  onEdit: (card: BoardCard) => void;
  onDelete: (cardId: string) => void;
  /** Opens the "align chapter with structure" reconciliation modal for this card. */
  onReconcile?: (card: BoardCard) => void;
  isDragOverlay?: boolean;
}

function BoardCardItem({ card, onEdit, onDelete, onReconcile, isDragOverlay }: BoardCardItemProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: card.id,
    data: { type: 'card', card },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const colorClasses = getCardColorClasses(card.color);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative rounded-xl border p-3 cursor-pointer transition-all select-none',
        colorClasses.bg,
        colorClasses.border,
        isDragging && 'opacity-40',
        isDragOverlay && 'shadow-lg rotate-1 scale-105',
        'hover:shadow-md hover:border-primary/40'
      )}
      onClick={() => onEdit(card)}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="absolute top-2 right-2 p-1 rounded text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing touch-none hover:text-muted-foreground"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical size={12} />
      </button>

      {/* Delete button */}
      <button
        className="absolute bottom-2 right-2 p-1 rounded text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive hover:bg-destructive/10"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(card.id);
        }}
      >
        <Trash2 size={12} />
      </button>

      <p className="text-sm font-medium text-foreground leading-snug pr-6">{card.title}</p>

      {card.notes && (
        <p className="mt-1.5 text-xs text-muted-foreground line-clamp-2 leading-relaxed">
          {card.notes}
        </p>
      )}

      {card.chapterId && (
        <div className="mt-2 flex items-center justify-between gap-1">
          <div className="flex items-center gap-1 min-w-0">
            <StickyNote size={10} className="text-muted-foreground/60 shrink-0" />
            <span className="text-[10px] text-muted-foreground/60 font-mono truncate">{t('outlineBoard.linkedChapter', 'capítulo vinculado')}</span>
          </div>
          {onReconcile && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onReconcile(card);
              }}
              className="shrink-0 p-1 -m-1 rounded text-muted-foreground/50 hover:text-primary hover:bg-primary/10 transition-colors"
              title={t('outlineBoard.reconcileTooltip', 'Alinhar capítulo com a estrutura')}
            >
              <Sparkles size={11} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// Board Column (sortable)
// ============================================

interface BoardColumnItemProps {
  column: BoardColumn;
  cards: BoardCard[];
  onAddCard: (columnId: string) => void;
  onEditCard: (card: BoardCard) => void;
  onDeleteCard: (projectId: string, cardId: string) => void;
  onRenameColumn: (columnId: string, title: string) => void;
  onDeleteColumn: (columnId: string) => void;
  onReconcileCard?: (card: BoardCard) => void;
  isDragOverlay?: boolean;
  projectId: string;
  /** Position of the column — drives its cycling accent colour. */
  colorIndex?: number;
}

function BoardColumnItem({
  column,
  cards,
  onAddCard,
  onEditCard,
  onDeleteCard,
  onRenameColumn,
  onDeleteColumn,
  onReconcileCard,
  isDragOverlay,
  projectId,
  colorIndex = 0,
}: BoardColumnItemProps) {
  const { t } = useTranslation();
  const accent = COLUMN_ACCENTS[colorIndex % COLUMN_ACCENTS.length];
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(column.title);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const { setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
    data: { type: 'column', column },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  const handleSaveRename = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== column.title) {
      onRenameColumn(column.id, trimmed);
    } else {
      setRenameValue(column.title);
    }
    setIsRenaming(false);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        // Desktop-only now (mobile renders MobileColumnSection instead) — fixed width kanban column.
        'flex flex-col w-72 shrink-0 rounded-[16px] border-[1.5px] overflow-hidden transition-all',
        accent,
        isDragging && 'opacity-40',
        isDragOverlay && 'shadow-2xl rotate-1'
      )}
    >
      {/* Column header */}
      <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-2">
        {isRenaming ? (
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={handleSaveRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveRename();
                if (e.key === 'Escape') {
                  setRenameValue(column.title);
                  setIsRenaming(false);
                }
              }}
              className="flex-1 min-w-0 text-sm font-semibold bg-background border border-border rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <button
              onClick={handleSaveRename}
              className="p-1 hover:bg-[rgba(94,234,212,0.12)] rounded" style={{ color: 'var(--aurora)' }}
            >
              <Check size={13} />
            </button>
            <button
              onClick={() => { setRenameValue(column.title); setIsRenaming(false); }}
              className="p-1 text-muted-foreground hover:bg-muted rounded"
            >
              <X size={13} />
            </button>
          </div>
        ) : (
          <>
            <h3
              className="flex-1 font-mono text-[9px] font-medium uppercase tracking-[0.18em] leading-relaxed text-foreground/90 truncate cursor-pointer hover:text-primary transition-colors"
              onDoubleClick={() => setIsRenaming(true)}
              title={t('outlineBoard.doubleClickToRename', 'Duplo clique para renomear')}
            >
              {column.title}
            </h3>
            <span className="text-xs text-muted-foreground font-mono shrink-0">{cards.length}</span>
            <DropdownMenu>
              <DropdownMenuTrigger
                className="p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <Pencil size={13} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setIsRenaming(true)}>
                  <Pencil size={13} />
                  {t('outlineBoard.rename', 'Renomear')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive data-[highlighted]:text-destructive"
                  onClick={() => onDeleteColumn(column.id)}
                >
                  <Trash2 size={13} />
                  {t('outlineBoard.deleteColumn', 'Excluir coluna')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>

      {/* Cards list */}
      <div className="flex-1 overflow-y-auto px-3 pb-2 space-y-2 min-h-[80px]">
        <SortableContext
          items={cards.map((c) => c.id)}
          strategy={verticalListSortingStrategy}
        >
          {cards.map((card) => (
            <BoardCardItem
              key={card.id}
              card={card}
              columnId={column.id}
              onEdit={onEditCard}
              onDelete={(cardId) => onDeleteCard(projectId, cardId)}
              onReconcile={onReconcileCard}
            />
          ))}
        </SortableContext>

        {cards.length === 0 && (
          <p className="text-center text-xs text-muted-foreground/40 py-4 font-mono tracking-wide">
            {t('outlineBoard.noScenesYet', 'Sem cenas. Crie a primeira abaixo.')}
          </p>
        )}
      </div>

      {/* Add card button */}
      <div className="px-3 pb-3">
        <button
          onClick={() => onAddCard(column.id)}
          className="w-full flex items-center justify-center gap-1.5 rounded-[9px] border border-dashed border-foreground/25 p-2 font-mono text-[8.5px] uppercase tracking-[0.16em] text-muted-foreground hover:border-primary/60 hover:text-primary hover:bg-primary/5 transition-all"
        >
          <Plus size={13} />
          {t('outlineBoard.addScene', 'Adicionar cena')}
        </button>
      </div>
    </div>
  );
}

// ============================================
// Mobile Column Section — vertical accordion (<md replacement for the kanban column)
// ============================================

interface MobileColumnSectionProps {
  column: BoardColumn;
  cards: BoardCard[];
  collapsed: boolean;
  onToggleCollapse: () => void;
  onAddCard: (columnId: string) => void;
  onEditCard: (card: BoardCard) => void;
  onDeleteCard: (projectId: string, cardId: string) => void;
  onRenameColumn: (columnId: string, title: string) => void;
  onDeleteColumn: (columnId: string) => void;
  onReconcileCard?: (card: BoardCard) => void;
  isDragOverlay?: boolean;
  projectId: string;
  /** Position of the column — drives its cycling accent colour. */
  colorIndex?: number;
}

function MobileColumnSection({
  column,
  cards,
  collapsed,
  onToggleCollapse,
  onAddCard,
  onEditCard,
  onDeleteCard,
  onRenameColumn,
  onDeleteColumn,
  onReconcileCard,
  projectId,
  colorIndex = 0,
}: MobileColumnSectionProps) {
  const { t } = useTranslation();
  const accent = COLUMN_ACCENTS[colorIndex % COLUMN_ACCENTS.length];
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(column.title);
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Registers this section as a valid drop target for cross-column card drags.
  // Column reordering isn't exposed on mobile (only card D&D within/between
  // sections), so this is intentionally droppable-only — no drag handle.
  const { setNodeRef } = useDroppable({ id: column.id, data: { type: 'column', column } });

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  const handleSaveRename = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== column.title) {
      onRenameColumn(column.id, trimmed);
    } else {
      setRenameValue(column.title);
    }
    setIsRenaming(false);
  };

  return (
    <div ref={setNodeRef} className={cn('rounded-[16px] border-[1.5px] overflow-hidden transition-all', accent)}>
      {/* Section header — tap the title/chevron to collapse or expand */}
      <div className="flex items-center gap-2 px-3 py-3">
        <button
          type="button"
          onClick={onToggleCollapse}
          className="shrink-0 p-0.5 text-muted-foreground/70 hover:text-foreground transition-colors"
          aria-label={collapsed ? t('outlineBoard.expandColumn', 'Expandir') : t('outlineBoard.collapseColumn', 'Recolher')}
          aria-expanded={!collapsed}
        >
          <ChevronDown size={16} className={cn('transition-transform', collapsed && '-rotate-90')} />
        </button>

        {isRenaming ? (
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={handleSaveRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveRename();
                if (e.key === 'Escape') {
                  setRenameValue(column.title);
                  setIsRenaming(false);
                }
              }}
              className="flex-1 min-w-0 text-sm font-semibold bg-background border border-border rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <button
              onClick={handleSaveRename}
              className="p-1 hover:bg-[rgba(94,234,212,0.12)] rounded" style={{ color: 'var(--aurora)' }}
            >
              <Check size={13} />
            </button>
            <button
              onClick={() => { setRenameValue(column.title); setIsRenaming(false); }}
              className="p-1 text-muted-foreground hover:bg-muted rounded"
            >
              <X size={13} />
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={onToggleCollapse}
              className="flex-1 min-w-0 text-left font-mono text-[10px] font-medium uppercase tracking-[0.18em] leading-relaxed text-foreground/90 truncate"
            >
              {column.title}
            </button>
            <span className="text-xs text-muted-foreground font-mono shrink-0">{cards.length}</span>
            <DropdownMenu>
              <DropdownMenuTrigger
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                onClick={(e) => e.stopPropagation()}
              >
                <Pencil size={14} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setIsRenaming(true)}>
                  <Pencil size={13} />
                  {t('outlineBoard.rename', 'Renomear')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive data-[highlighted]:text-destructive"
                  onClick={() => onDeleteColumn(column.id)}
                >
                  <Trash2 size={13} />
                  {t('outlineBoard.deleteColumn', 'Excluir coluna')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        )}
      </div>

      {/* Cards — vertical list, collapsible */}
      {!collapsed && (
        <div className="px-3 pb-3 space-y-2">
          <SortableContext
            items={cards.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            {cards.map((card) => (
              <BoardCardItem
                key={card.id}
                card={card}
                columnId={column.id}
                onEdit={onEditCard}
                onDelete={(cardId) => onDeleteCard(projectId, cardId)}
                onReconcile={onReconcileCard}
              />
            ))}
          </SortableContext>

          {cards.length === 0 && (
            <p className="text-center text-xs text-muted-foreground/40 py-3 font-mono tracking-wide">
              {t('outlineBoard.noScenesYet', 'Sem cenas. Crie a primeira abaixo.')}
            </p>
          )}

          {/* Add card button — clear per-section action */}
          <button
            onClick={() => onAddCard(column.id)}
            className="w-full flex items-center justify-center gap-1.5 rounded-[9px] border border-dashed border-foreground/25 p-2.5 font-mono text-[9px] uppercase tracking-[0.16em] text-muted-foreground hover:border-primary/60 hover:text-primary hover:bg-primary/5 transition-all"
          >
            <Plus size={13} />
            {t('outlineBoard.addScene', 'Adicionar cena')}
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Outline Board View
// ============================================

export function OutlineBoardView({ projectId: embeddedProjectId }: { projectId?: string } = {}) {
  const { t } = useTranslation();
  const { projectId: paramProjectId } = useParams<{ projectId: string }>();
  const projectId = embeddedProjectId ?? paramProjectId;
  const embedded = !!embeddedProjectId;
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const projects = useAppStore((s) => s.projects);

  const project = projects.find((p) => p.id === projectId);
  const chapters = project?.chapters ?? [];

  const [columns, setColumns] = useState<BoardColumn[]>([]);
  const [loading, setLoading] = useState(true);
  // Mobile accordion: which columns are collapsed (default: all expanded)
  const [collapsedColumns, setCollapsedColumns] = useState<Set<string>>(new Set());
  const toggleColumnCollapsed = useCallback((columnId: string) => {
    setCollapsedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnId)) next.delete(columnId);
      else next.add(columnId);
      return next;
    });
  }, []);

  // For DnD overlay
  const [activeCard, setActiveCard] = useState<BoardCard | null>(null);
  const [activeColumn, setActiveColumn] = useState<BoardColumn | null>(null);

  // Card edit modal state
  const [editingCard, setEditingCard] = useState<BoardCard | null>(null);
  const [addingToColumn, setAddingToColumn] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Add column UI
  const [addingColumn, setAddingColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const newColumnInputRef = useRef<HTMLInputElement>(null);

  // View mode toggle: kanban board vs horizontal timeline

  // Template picker state
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  // a teia da trama: 3+ colunas, todas com pelo menos uma cena (1x por projeto)
  useEffect(() => {
    if (!projectId || columns.length < 3) return;
    const key = `moment-weave-${projectId}`;
    if (localStorage.getItem(key)) return;
    const standing = columns.filter((c) => (c.cards?.length ?? 0) > 0);
    if (standing.length >= 3) {
      localStorage.setItem(key, '1');
      window.dispatchEvent(new CustomEvent('reformu:moment', { detail: { type: 'weave' } }));
    }
  }, [columns, projectId]);

  const fetchBoard = useCallback(async () => {
    if (!projectId) return;
    try {
      const { columns } = await api.getBoard(projectId);
      setColumns(columns);
    } catch {
      toast.error(t('outlineBoard.errorLoadingBoard', 'Erro ao carregar o quadro.'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  useEffect(() => {
    if (addingColumn && newColumnInputRef.current) {
      newColumnInputRef.current.focus();
    }
  }, [addingColumn]);

  // ============================================
  // Helpers
  // ============================================

  function getColumnCards(columnId: string): BoardCard[] {
    return columns.find((c) => c.id === columnId)?.cards ?? [];
  }

  function findColumnOfCard(cardId: string): BoardColumn | undefined {
    return columns.find((col) => col.cards.some((c) => c.id === cardId));
  }

  // ============================================
  // Column actions
  // ============================================

  const handleAddColumn = async () => {
    const title = newColumnTitle.trim();
    if (!title || !projectId) return;
    try {
      const { column } = await api.createColumn(projectId, title);
      setColumns((prev) => [...prev, { ...column, cards: [] }]);
      setNewColumnTitle('');
      setAddingColumn(false);
    } catch {
      toast.error(t('outlineBoard.errorCreatingColumn', 'Erro ao criar coluna.'));
    }
  };

  // Add rápido pela barra do topo (padrão Universo): cria uma coluna com nome
  // padrão que o usuário renomeia — sem depender do input inline do rodapé.
  const handleQuickAddColumn = async () => {
    if (!projectId) return;
    try {
      const { column } = await api.createColumn(projectId, t('outlineBoard.newColumnDefault', 'Novo ato'));
      setColumns((prev) => [...prev, { ...column, cards: [] }]);
    } catch {
      toast.error(t('outlineBoard.errorCreatingColumn', 'Erro ao criar coluna.'));
    }
  };

  const handleRenameColumn = async (columnId: string, title: string) => {
    if (!projectId) return;
    // Optimistic
    setColumns((prev) =>
      prev.map((c) => (c.id === columnId ? { ...c, title } : c))
    );
    try {
      await api.updateColumn(projectId, columnId, title);
    } catch {
      toast.error(t('outlineBoard.errorRenamingColumn', 'Erro ao renomear coluna.'));
      fetchBoard();
    }
  };

  const handleDeleteColumn = async (columnId: string) => {
    if (!projectId) return;
    // Optimistic
    setColumns((prev) => prev.filter((c) => c.id !== columnId));
    try {
      await api.deleteColumn(projectId, columnId);
    } catch {
      toast.error(t('outlineBoard.errorDeletingColumn', 'Erro ao excluir coluna.'));
      fetchBoard();
    }
  };

  // ============================================
  // Template actions
  // ============================================

  const handleApplyTemplate = async (
    template: StoryStructure,
    mode: 'replace' | 'append'
  ): Promise<void> => {
    if (!projectId) return;
    setApplyingTemplate(true);
    try {
      if (mode === 'replace') {
        // Delete every existing column — FK cascade removes their cards
        const snapshot = [...columns];
        for (const col of snapshot) {
          await api.deleteColumn(projectId, col.id);
        }
        setColumns([]);
      }

      const newCols: BoardColumn[] = [];
      for (const [colIndex, tCol] of template.columns.entries()) {
        const columnTitle = t(`storyStructures.${template.id}.columns.${colIndex}.title`, tCol.title);
        const { column } = await api.createColumn(projectId, columnTitle);
        const newCards: BoardCard[] = [];
        for (const [beatIndex, beat] of tCol.beats.entries()) {
          const { card } = await api.createCard(projectId, {
            columnId: column.id,
            title: t(
              `storyStructures.${template.id}.columns.${colIndex}.beats.${beatIndex}.title`,
              beat.title
            ),
            notes: t(
              `storyStructures.${template.id}.columns.${colIndex}.beats.${beatIndex}.description`,
              beat.description
            ),
          });
          newCards.push(card);
        }
        newCols.push({ ...column, cards: newCards });
      }

      setColumns((prev) =>
        mode === 'replace' ? newCols : [...prev, ...newCols]
      );
      toast.success(
        t('outlineBoard.templateApplied', 'Template "{{name}}" aplicado!', {
          name: t(`storyStructures.${template.id}.name`, template.name),
        })
      );
    } catch {
      toast.error(t('outlineBoard.errorApplyingTemplate', 'Erro ao aplicar o template.'));
      fetchBoard();
    } finally {
      setApplyingTemplate(false);
    }
  };

  // ============================================
  // Card actions
  // ============================================

  const openAddCard = (columnId: string) => {
    setEditingCard(null);
    setAddingToColumn(columnId);
    setModalOpen(true);
  };

  const openEditCard = (card: BoardCard) => {
    setEditingCard(card);
    setAddingToColumn(null);
    setModalOpen(true);
  };

  const handleSaveCard = async (data: {
    title: string;
    notes: string | null;
    color: string | null;
    chapterId: string | null;
  }) => {
    if (!projectId) return;

    if (editingCard) {
      // Update
      const optimisticCard = { ...editingCard, ...data };
      setColumns((prev) =>
        prev.map((col) => ({
          ...col,
          cards: col.cards.map((c) => (c.id === editingCard.id ? optimisticCard : c)),
        }))
      );
      try {
        await api.updateCard(projectId, editingCard.id, data);
      } catch {
        toast.error(t('outlineBoard.errorSavingScene', 'Erro ao salvar cena.'));
        fetchBoard();
      }
    } else if (addingToColumn) {
      // Create
      try {
        const { card } = await api.createCard(projectId, {
          columnId: addingToColumn,
          ...data,
        });
        setColumns((prev) =>
          prev.map((col) =>
            col.id === addingToColumn ? { ...col, cards: [...col.cards, card] } : col
          )
        );
      } catch {
        toast.error(t('outlineBoard.errorCreatingScene', 'Erro ao criar cena.'));
      }
    }
  };

  const handleDeleteCard = async (colProjectId: string, cardId: string) => {
    const col = findColumnOfCard(cardId);
    if (!col) return;
    // Optimistic
    setColumns((prev) =>
      prev.map((c) =>
        c.id === col.id ? { ...c, cards: c.cards.filter((card) => card.id !== cardId) } : c
      )
    );
    try {
      await api.deleteCard(colProjectId, cardId);
    } catch {
      toast.error(t('outlineBoard.errorDeletingScene', 'Erro ao excluir cena.'));
      fetchBoard();
    }
  };

  // ============================================
  // Structure reconciliation ("align chapter with structure")
  // ============================================

  const updateChapterInStore = useAppStore((s) => s.updateChapter);

  const [reconcileCard, setReconcileCard] = useState<BoardCard | null>(null);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileResult, setReconcileResult] = useState<{
    diverges: boolean;
    summary: string;
    reconciledContent: string;
  } | null>(null);
  const [reconcileApplying, setReconcileApplying] = useState(false);

  const closeReconcile = () => {
    setReconcileOpen(false);
    setReconcileCard(null);
    setReconcileResult(null);
    setReconcileLoading(false);
  };

  const openReconcile = async (card: BoardCard) => {
    if (!projectId || !card.chapterId) return;
    setReconcileCard(card);
    setReconcileResult(null);
    setReconcileOpen(true);
    setReconcileLoading(true);
    try {
      const result = await api.reconcileChapter(
        projectId,
        card.chapterId,
        `${card.title}\n${card.notes ?? ''}`.trim()
      );
      setReconcileResult(result);
    } catch (error) {
      if (error instanceof AiLimitError) {
        toast.error(
          t(
            'outlineBoard.reconcileLimitError',
            'Você atingiu o limite de ações de IA do seu plano este mês. Faça upgrade para continuar.'
          )
        );
      } else {
        toast.error(t('outlineBoard.reconcileError', 'Erro ao verificar o alinhamento do capítulo.'));
      }
      closeReconcile();
    } finally {
      setReconcileLoading(false);
    }
  };

  const applyReconcile = async () => {
    if (!projectId || !reconcileCard?.chapterId || !reconcileResult) return;
    setReconcileApplying(true);
    try {
      await api.updateChapter(reconcileCard.chapterId, { content: reconcileResult.reconciledContent });
      updateChapterInStore(projectId, reconcileCard.chapterId, { content: reconcileResult.reconciledContent });
      toast.success(t('outlineBoard.reconcileApplied', 'Capítulo realinhado com a estrutura.'));
      closeReconcile();
    } catch {
      toast.error(t('outlineBoard.reconcileApplyError', 'Erro ao aplicar a reescrita.'));
    } finally {
      setReconcileApplying(false);
    }
  };

  // ============================================
  // DnD sensors
  // ============================================

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: 150, tolerance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ============================================
  // DnD handlers
  // ============================================

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as { type: string; card?: BoardCard; column?: BoardColumn };
    if (data.type === 'card') setActiveCard(data.card ?? null);
    if (data.type === 'column') setActiveColumn(data.column ?? null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeData = active.data.current as { type: string; card?: BoardCard };
    if (activeData.type !== 'card') return;

    const activeCardId = active.id as string;
    const overId = over.id as string;

    const overData = over.data.current as { type?: string; card?: BoardCard };

    const activeCol = findColumnOfCard(activeCardId);
    if (!activeCol) return;

    // Are we over a card or a column?
    let overCol: BoardColumn | undefined;
    if (overData?.type === 'card') {
      overCol = findColumnOfCard(overId);
    } else {
      overCol = columns.find((c) => c.id === overId);
    }

    if (!overCol || activeCol.id === overCol.id) return;

    // Move card optimistically between columns
    setColumns((prev) => {
      const card = activeCol.cards.find((c) => c.id === activeCardId);
      if (!card) return prev;

      const overCardIndex = overData?.type === 'card'
        ? overCol!.cards.findIndex((c) => c.id === overId)
        : overCol!.cards.length;

      return prev.map((col) => {
        if (col.id === activeCol.id) {
          return { ...col, cards: col.cards.filter((c) => c.id !== activeCardId) };
        }
        if (col.id === overCol!.id) {
          const newCards = [...col.cards];
          newCards.splice(overCardIndex >= 0 ? overCardIndex : newCards.length, 0, {
            ...card,
            columnId: overCol!.id,
          });
          return { ...col, cards: newCards };
        }
        return col;
      });
    });
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveCard(null);
    setActiveColumn(null);

    const { active, over } = event;
    if (!over || !projectId) return;

    const activeData = active.data.current as { type: string };
    const activeId = active.id as string;
    const overId = over.id as string;

    // Column reorder
    if (activeData.type === 'column') {
      if (activeId === overId) return;
      const oldIndex = columns.findIndex((c) => c.id === activeId);
      const newIndex = columns.findIndex((c) => c.id === overId);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(columns, oldIndex, newIndex);
      setColumns(reordered);

      try {
        await api.reorderColumns(projectId, reordered.map((c) => c.id));
      } catch {
        toast.error(t('outlineBoard.errorReorderingColumns', 'Erro ao reordenar colunas.'));
        fetchBoard();
      }
      return;
    }

    // Card drop
    if (activeData.type === 'card') {
      const overData = over.data.current as { type?: string };

      // Find the final column of the card after drag
      const destCol = overData?.type === 'card'
        ? findColumnOfCard(overId)
        : columns.find((c) => c.id === overId);

      if (!destCol) return;

      const srcColBeforeMove = findColumnOfCard(activeId) ?? destCol;
      const destCards = destCol.cards;
      const cardIndex = destCards.findIndex((c) => c.id === activeId);

      // Reorder within same column
      if (srcColBeforeMove.id === destCol.id) {
        const overCardIndex = destCards.findIndex((c) => c.id === overId);
        if (cardIndex === overCardIndex || overCardIndex === -1) {
          // Persist current order
          try {
            await api.reorderCards(projectId, destCol.id, destCards.map((c) => c.id));
          } catch {
            toast.error(t('outlineBoard.errorReorderingScenes', 'Erro ao reordenar cenas.'));
            fetchBoard();
          }
          return;
        }
        const reorderedCards = arrayMove(destCards, cardIndex, overCardIndex);
        setColumns((prev) =>
          prev.map((col) => (col.id === destCol.id ? { ...col, cards: reorderedCards } : col))
        );
        try {
          await api.reorderCards(projectId, destCol.id, reorderedCards.map((c) => c.id));
        } catch {
          toast.error(t('outlineBoard.errorReorderingScenes', 'Erro ao reordenar cenas.'));
          fetchBoard();
        }
      } else {
        // Cross-column move — the card is already optimistically in destCol from handleDragOver
        try {
          await api.moveCard(projectId, activeId, destCol.id, cardIndex >= 0 ? cardIndex : 0);
        } catch {
          toast.error(t('outlineBoard.errorMovingScene', 'Erro ao mover cena.'));
          fetchBoard();
        }
      }
    }
  };

  // ============================================
  // Render
  // ============================================

  // No book chosen → show a picker instead of a dead-end message.
  if (!projectId) {
    return (
      <div className="flex flex-col h-full min-h-0 bg-background">
        <div className="flex-shrink-0 bg-card px-6 pt-4 border-b border-border">
          <PageHeader
            icon={<LayoutDashboard size={20} />}
            title={t('outlineBoard.pageTitle', 'Estrutura')}
            subtitle={t('outlineBoard.chooseBookSubtitle', 'Escolha um livro para começar')}
            className="border-b-0 mb-0 md:mb-0"
          />
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-6">
          {projects.length === 0 ? (
            <EmptyState
              icon={<LayoutGrid size={36} />}
              title={t('outlineBoard.noBooksYet', 'Nenhum livro na estante ainda.')}
              description={t('outlineBoard.noBooksYetDescription', 'Crie um livro primeiro — a estrutura de atos e cenas vive dentro dele.')}
              action={
                <button
                  onClick={() => navigate('/app/projects')}
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
                >
                  <Plus size={14} />
                  {t('outlineBoard.createFirstBook', 'Criar meu primeiro livro')}
                </button>
              }
              className="h-64 py-0"
            />
          ) : (
            <div className="mx-auto max-w-xl grid grid-cols-1 gap-2.5">
              {projects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/app/outline/${p.id}`)}
                  className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-3 text-left transition-all hover:border-primary/40 hover:shadow-md"
                >
                  <div className="h-14 w-10 shrink-0 overflow-hidden rounded-md shadow-sm ring-1 ring-border">
                    <BookCover
                      title={p.metadata.title || t('outlineBoard.untitled', 'Sem título')}
                      author={p.metadata.author || ''}
                      genre={p.metadata.genre || ''}
                      coverImage={p.metadata.coverImage}
                      variant="cover"
                      seed={p.id}
                      progress={0}
                      disableTilt
                      className="h-full w-full"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-foreground truncate">
                      {p.metadata.title || t('outlineBoard.untitled', 'Sem título')}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {p.metadata.author || t('outlineBoard.noAuthor', 'Sem autor')}
                    </p>
                  </div>
                  <ChevronRight
                    size={16}
                    className="text-muted-foreground shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-background">
      {/* Header — only standalone. Embedded in the editor workspace, the shared
          tab bar (Editor · Universo · Estrutura) is the header, so we hide this one
          to avoid a duplicate title and a stray, orphaned template button. */}
      {!embedded && (
      <div className="flex-shrink-0 bg-card px-6 pt-4 border-b border-border">
        <PageHeader
          icon={<LayoutDashboard size={20} />}
          title={t('outlineBoard.pageTitle', 'Estrutura')}
          subtitle={t('outlineBoard.pageSubtitle', 'Organize atos, cenas e capítulos')}
          className="border-b-0 mb-0 md:mb-0"
          actions={
            <>
              {/* Book switcher — non-embedded only */}
              {!embedded && (
                <Select value={projectId} onValueChange={(id) => navigate(`/app/outline/${id}`)}>
                  <SelectTrigger className="h-8 w-auto max-w-[160px] gap-1 rounded-lg border border-border bg-muted/40 px-2 py-0 text-xs font-medium text-foreground focus:ring-0 shrink-0">
                    <span className="truncate">
                      {project?.metadata?.title || t('outlineBoard.chooseBook', 'Escolher livro')}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.metadata.title || t('outlineBoard.untitled', 'Sem título')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {/* Template picker button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTemplatePickerOpen(true)}
                disabled={applyingTemplate || loading}
                className="gap-1.5 text-xs shrink-0"
              >
                {applyingTemplate ? (
                  <Loader2 size={13} className="animate-spin" />
                ) : (
                  <Wand2 size={13} />
                )}
                <span className="hidden sm:inline">{t('outlineBoard.pageTitle', 'Estrutura')}</span>
              </Button>
            </>
          }
        />
      </div>
      )}

      {/* Barra de ações no topo (embedded) — mesmo padrão do Universo:
          Adicionar coluna (fantasma, esquerda) · Aplicar estrutura pronta
          (primária brasa, direita). */}
      {embedded && (
        <div className="flex-shrink-0 flex items-center justify-between gap-2 px-4 pt-3">
          <button
            type="button"
            onClick={handleQuickAddColumn}
            className="btn-ghost-mock inline-flex items-center gap-1.5 shrink-0"
            style={{ padding: '8px 14px', fontSize: '9px' }}
          >
            <Plus size={14} />
            {t('outlineBoard.addColumn', 'Adicionar coluna')}
          </button>
          <button
            type="button"
            onClick={() => setTemplatePickerOpen(true)}
            disabled={applyingTemplate || loading}
            className="btn-ember shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ padding: '8px 16px', fontSize: '9.5px' }}
          >
            {applyingTemplate ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            {t('outlineBoard.applyReadyStructure', 'Aplicar estrutura pronta')}
          </button>
        </div>
      )}

      {/* Board / Timeline */}
      {loading ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/50" />
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/40">
            {t('outlineBoard.loadingStructure', 'Carregando estrutura…')}
          </p>
        </div>
      ) : isMobile ? (
        // Mobile (<md) — vertical accordion: each column stacks as a collapsible
        // section with its cards listed underneath, natural vertical scroll (no
        // horizontal kanban carousel). Card D&D (within/between sections) still
        // works; column reordering isn't exposed here (not needed on mobile).
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            {columns.map((column, idx) => (
              <MobileColumnSection
                key={column.id}
                column={column}
                cards={column.cards}
                colorIndex={idx}
                collapsed={collapsedColumns.has(column.id)}
                onToggleCollapse={() => toggleColumnCollapsed(column.id)}
                onAddCard={openAddCard}
                onEditCard={openEditCard}
                onDeleteCard={handleDeleteCard}
                onRenameColumn={handleRenameColumn}
                onDeleteColumn={handleDeleteColumn}
                onReconcileCard={openReconcile}
                projectId={projectId}
              />
            ))}

            <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
              {activeCard && (
                <BoardCardItem
                  card={activeCard}
                  columnId={activeCard.columnId}
                  onEdit={() => {}}
                  onDelete={() => {}}
                  isDragOverlay
                />
              )}
            </DragOverlay>
          </DndContext>

          {/* Add column — full-width section at the end of the list */}
          {addingColumn ? (
            <div className="rounded-2xl bg-muted/50 border border-border/60 p-3 space-y-2">
              <input
                ref={newColumnInputRef}
                value={newColumnTitle}
                onChange={(e) => setNewColumnTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddColumn();
                  if (e.key === 'Escape') { setAddingColumn(false); setNewColumnTitle(''); }
                }}
                placeholder={t('outlineBoard.columnNamePlaceholder', 'Nome da coluna...')}
                className="w-full text-sm bg-background border border-border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddColumn} disabled={!newColumnTitle.trim()}>
                  {t('outlineBoard.create', 'Criar')}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setAddingColumn(false); setNewColumnTitle(''); }}
                >
                  {t('outlineBoard.cancel', 'Cancelar')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <button
                onClick={() => setAddingColumn(true)}
                className="w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border/50 p-4 text-sm text-muted-foreground hover:border-primary/50 hover:text-primary hover:bg-primary/5 transition-all"
              >
                <Plus size={16} />
                {t('outlineBoard.addColumn', 'Adicionar coluna')}
              </button>
            </div>
          )}
        </div>
      ) : (
        // Desktop (md+) — free-scrolling horizontal kanban, unchanged.
        <div className="flex-1 overflow-x-auto overflow-y-hidden scroll-smooth [-webkit-overflow-scrolling:touch]">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="flex flex-row gap-3 md:gap-4 p-3 md:p-4 h-full items-start">
              {/* Columns */}
              <SortableContext
                items={columns.map((c) => c.id)}
                strategy={horizontalListSortingStrategy}
              >
                {columns.map((column, idx) => (
                  <BoardColumnItem
                    key={column.id}
                    column={column}
                    cards={column.cards}
                    colorIndex={idx}
                    onAddCard={openAddCard}
                    onEditCard={openEditCard}
                    onDeleteCard={handleDeleteCard}
                    onRenameColumn={handleRenameColumn}
                    onDeleteColumn={handleDeleteColumn}
                    onReconcileCard={openReconcile}
                    projectId={projectId}
                  />
                ))}
              </SortableContext>

              {/* Add column */}
              <div className="w-72 shrink-0">
                {addingColumn ? (
                  <div className="rounded-2xl bg-muted/50 border border-border/60 p-3 space-y-2">
                    <input
                      ref={newColumnInputRef}
                      value={newColumnTitle}
                      onChange={(e) => setNewColumnTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddColumn();
                        if (e.key === 'Escape') { setAddingColumn(false); setNewColumnTitle(''); }
                      }}
                      placeholder={t('outlineBoard.columnNamePlaceholder', 'Nome da coluna...')}
                      className="w-full text-sm bg-background border border-border rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleAddColumn} disabled={!newColumnTitle.trim()}>
                        {t('outlineBoard.create', 'Criar')}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setAddingColumn(false); setNewColumnTitle(''); }}
                      >
                        {t('outlineBoard.cancel', 'Cancelar')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      onClick={() => setAddingColumn(true)}
                      className="w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-border/50 p-4 text-sm text-muted-foreground hover:border-primary/50 hover:text-primary hover:bg-primary/5 transition-all"
                    >
                      <Plus size={16} />
                      {t('outlineBoard.addColumn', 'Adicionar coluna')}
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Drag overlay */}
            <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
              {activeCard && (
                <BoardCardItem
                  card={activeCard}
                  columnId={activeCard.columnId}
                  onEdit={() => {}}
                  onDelete={() => {}}
                  isDragOverlay
                />
              )}
              {activeColumn && !activeCard && (
                <BoardColumnItem
                  column={activeColumn}
                  cards={activeColumn.cards}
                  onAddCard={() => {}}
                  onEditCard={() => {}}
                  onDeleteCard={() => {}}
                  onRenameColumn={() => {}}
                  onDeleteColumn={() => {}}
                  isDragOverlay
                  projectId={projectId}
                />
              )}
            </DragOverlay>
          </DndContext>
        </div>
      )}

      {/* Card edit/create modal */}
      <CardEditModal
        card={editingCard}
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingCard(null); setAddingToColumn(null); }}
        onSave={handleSaveCard}
        chapters={chapters.map((ch) => ({ id: ch.id, title: ch.title }))}
      />

      {/* Template picker modal */}
      <TemplatePickerModal
        open={templatePickerOpen}
        onClose={() => setTemplatePickerOpen(false)}
        onApply={handleApplyTemplate}
        hasExistingContent={columns.length > 0}
        applying={applyingTemplate}
      />

      {/* Structure reconciliation modal — "align chapter with structure" */}
      <ResponsiveModal open={reconcileOpen} onOpenChange={(o) => !o && closeReconcile()}>
        <ResponsiveModalContent className="sm:max-w-md">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {t('outlineBoard.reconcileTitle', 'Alinhar capítulo com a estrutura')}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              {reconcileLoading
                ? t('outlineBoard.reconcileChecking', 'Verificando se o capítulo ainda está alinhado com a nova cena...')
                : reconcileResult
                  ? reconcileResult.summary || t('outlineBoard.reconcileNoDivergence', 'Nenhuma divergência — o capítulo já está alinhado.')
                  : ''}
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          {reconcileLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-muted-foreground" />
            </div>
          )}

          <ResponsiveModalFooter>
            {!reconcileLoading && reconcileResult && !reconcileResult.diverges && (
              <Button onClick={closeReconcile}>{t('outlineBoard.close', 'Fechar')}</Button>
            )}
            {!reconcileLoading && reconcileResult?.diverges && (
              <>
                <Button variant="outline" onClick={closeReconcile} disabled={reconcileApplying}>
                  {t('outlineBoard.cancel', 'Cancelar')}
                </Button>
                <Button onClick={applyReconcile} disabled={reconcileApplying}>
                  {reconcileApplying ? <Loader2 size={14} className="animate-spin mr-2" /> : null}
                  {t('outlineBoard.reconcileAccept', 'Aceitar e reescrever')}
                </Button>
              </>
            )}
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>
    </div>
  );
}
